/**
 * Returns true when a room should count toward billing.
 * Shadow rooms (secondary-occupant placeholders for an existing bedroom — see
 * `isPrimaryRoom` for the full explanation) are excluded so the same physical
 * bedroom is never double-counted against a host's subscription.
 * Bathrooms are excluded by the roomType check.
 */
export function isBillableRoom(room: { roomType: string | null; shadowOfRoomId?: number | null }): boolean {
  return room.roomType === "bedroom" && (room.shadowOfRoomId == null);
}

/**
 * Per-room monthly subscription rates (in US dollars) for paid account tiers.
 * Shared between the marketing site, sign-up flow, and any other pricing
 * display so prices are never duplicated across the codebase.
 */
export const PLAN_PER_ROOM_RATES = {
  Growth:       { monthly: 12, annual: 10 },
  Professional: { monthly: 18, annual: 15 },
} as const;

/**
 * Returns true when a row in the `rooms` table represents a real, physical
 * room — i.e. NOT a shadow row.
 *
 * What a shadow row actually is:
 *   PadSplit enforces a one-member-per-room constraint, but in real life a
 *   bedroom can have two occupants (double occupancy). To model this without
 *   violating PadSplit's invariant, PadPulse inserts a second `rooms` row
 *   whose `shadowOfRoomId` points at the primary room. The shadow row exists
 *   purely as a member-assignment slot for the secondary occupant — it is not
 *   a separate physical space, it does not bill, and it does not store its
 *   own listing data.
 *
 * What a shadow row is NOT:
 *   Shadow rows are NOT used to store additional images for a room. Per-room
 *   images live on `rooms.inputImageUrl` / `rooms.outputImageUrl`, additional
 *   AI variants live in the `room_image_variants` table, and general property
 *   photos live in the `property_images` table. None of these reuse the
 *   shadow-row pattern.
 *
 * Use this helper to filter any room picker, dropdown, count, or listing
 * that should show each physical space exactly once. Unlike `isBillableRoom`,
 * this includes bathrooms and other non-bedroom room types — it only excludes
 * the shadow rows themselves.
 */
export function isPrimaryRoom(room: { shadowOfRoomId?: number | null }): boolean {
  return room.shadowOfRoomId == null;
}

/**
 * Vendor payment fee model — single source of truth.
 *
 * PadPulse charges a flat 3% on both sides of a vendor payment:
 *   - Host is charged: vendorAmount + ceil(vendorAmount * 0.03)
 *   - Vendor receives: vendorAmount - ceil(vendorAmount * 0.03)
 *   - PadPulse keeps roughly 6% (the two ceiled fees) and absorbs Stripe's
 *     actual processing cost out of that.
 *
 * Rounding rule: each side's 3% is rounded UP to the nearest cent
 * independently. This means the host always pays at least 1¢ on top for
 * any positive amount, and the vendor always loses at least 1¢, which
 * keeps the platform fully covered on the smallest tipping amounts
 * (e.g. $0.33 -> $0.01 fee on each side instead of $0.00).
 */
export const PADPULSE_VENDOR_FEE_RATE = 0.03;

/**
 * One-time monthly marketplace fee charged when a vendor receives their first
 * marketplace-driven job in a calendar month. "Marketplace-driven" means the
 * task was created by a host OTHER than the vendor's original inviting host
 * (vendor.userId). Deducted from the vendor's payout; never charged to the
 * host. $0 if the vendor gets no marketplace jobs in a given month.
 */
export const MARKETPLACE_JOB_FEE_CENTS = 1900;

export interface VendorFeeBreakdown {
  vendorAmountCents: number;
  padpulseFeeCents: number;
  hostTotalCents: number;
  vendorPayoutCents: number;
}

/**
 * Compute the breakdown for a vendor payment of `vendorAmountCents`.
 *
 * Negative or non-finite inputs are clamped to 0 to avoid producing
 * nonsensical totals upstream. Inputs are floored to integer cents.
 */
export function computeVendorFeeBreakdown(vendorAmountCents: number): VendorFeeBreakdown {
  const clean = Number.isFinite(vendorAmountCents) ? Math.max(0, Math.floor(vendorAmountCents)) : 0;
  const padpulseFeeCents = Math.ceil(clean * PADPULSE_VENDOR_FEE_RATE);
  const hostTotalCents = clean + padpulseFeeCents;
  const vendorPayoutCents = Math.max(0, clean - padpulseFeeCents);
  return {
    vendorAmountCents: clean,
    padpulseFeeCents,
    hostTotalCents,
    vendorPayoutCents,
  };
}

/**
 * Task #4074 — Net amount a vendor actually receives for a task of
 * `taskTotalCents`, after the 3% PadPulse vendor-side deduction.
 *
 * Used to pre-fill the "Add Manual Payment" form: when a host pays a vendor
 * off-platform we still want the recorded amount to default to what the vendor
 * would have netted on the Stripe rail, so manual and platform payouts stay
 * comparable. No fee is actually charged for manual payments — this is purely
 * the suggested default.
 */
export function netVendorPayoutAmount(taskTotalCents: number): number {
  return computeVendorFeeBreakdown(taskTotalCents).vendorPayoutCents;
}

/**
 * Pro-tier fee breakdown: the 3% PadPulse vendor-side fee is waived.
 *
 * Pro vendors pay a flat $99/month subscription in exchange for:
 *   - No 3% deduction from their payout (vendor receives the full amount).
 *   - No $19 pay-on-job marketplace fee.
 *
 * The host still pays vendorAmount + 3% ("Stripe passthrough") so Stripe's
 * own processing cost is covered. PadPulse keeps only that host-side 3%
 * instead of the usual ~6%.
 *
 * `padpulseFeeCents` in the returned breakdown reflects the application_fee_amount
 * that will be sent to Stripe — i.e. only the host-side markup.
 */
export function computeVendorFeeBreakdownPro(vendorAmountCents: number): VendorFeeBreakdown {
  const clean = Number.isFinite(vendorAmountCents) ? Math.max(0, Math.floor(vendorAmountCents)) : 0;
  const hostSideFee = Math.ceil(clean * PADPULSE_VENDOR_FEE_RATE);
  const hostTotalCents = clean + hostSideFee;
  const vendorPayoutCents = clean;
  return {
    vendorAmountCents: clean,
    padpulseFeeCents: hostSideFee,
    hostTotalCents,
    vendorPayoutCents,
  };
}

/**
 * Trash (Collection) Service pricing — single source of truth.
 *
 * Recurring per-property monthly service fee:
 *   - $50/mo standard (trash only)
 *   - $65/mo when the property also has a separate recycle pickup day
 * One-time on-demand pickup: flat $100.
 *
 * All amounts are in integer cents.
 */
export const TRASH_MONTHLY_STANDARD_CENTS = 5000;
export const TRASH_MONTHLY_RECYCLE_CENTS = 6500;
export const TRASH_ON_DEMAND_CENTS = 10000;

/**
 * The recurring monthly fee for a property, picked automatically from whether
 * the host enabled a separate recycle pickup day.
 */
export function trashMonthlyFeeCents(recycleEnabled: boolean): number {
  return recycleEnabled ? TRASH_MONTHLY_RECYCLE_CENTS : TRASH_MONTHLY_STANDARD_CENTS;
}

export interface TrashProration {
  /** Prorated amount to charge for the first partial month, in cents. */
  proratedCents: number;
  /** Days billed in the first partial month (approval day → end of month, inclusive). */
  daysCharged: number;
  /** Total days in the approval month. */
  daysInMonth: number;
  /** First-of-month the prorated charge covers, as YYYY-MM-DD. */
  periodMonthStart: string;
  /** First day of the next month, as YYYY-MM-DD — when the first FULL charge is due. */
  nextBillingDate: string;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Format a year / 0-based month / day triple as YYYY-MM-DD. */
export function formatTrashDate(year: number, monthIndex0: number, day: number): string {
  return `${year}-${pad2(monthIndex0 + 1)}-${pad2(day)}`;
}

/**
 * Compute the first-month proration for a recurring trash enrollment that is
 * activated (provider-approved) on `approvalDate`. The host is billed per day
 * from the approval day through the end of that calendar month; the next full
 * monthly charge falls on the 1st of the following month.
 *
 * Uses the approval date's local calendar parts so the day-count matches what
 * the host sees on their statement.
 */
export function computeTrashProration(monthlyCents: number, approvalDate: Date): TrashProration {
  const year = approvalDate.getFullYear();
  const monthIndex0 = approvalDate.getMonth();
  const day = approvalDate.getDate();
  const daysInMonth = new Date(year, monthIndex0 + 1, 0).getDate();
  const daysCharged = Math.max(1, daysInMonth - day + 1);
  const cleanMonthly = Number.isFinite(monthlyCents) ? Math.max(0, Math.floor(monthlyCents)) : 0;
  const proratedCents = Math.round((cleanMonthly * daysCharged) / daysInMonth);
  const nextYear = monthIndex0 === 11 ? year + 1 : year;
  const nextMonthIndex0 = monthIndex0 === 11 ? 0 : monthIndex0 + 1;
  return {
    proratedCents,
    daysCharged,
    daysInMonth,
    periodMonthStart: formatTrashDate(year, monthIndex0, 1),
    nextBillingDate: formatTrashDate(nextYear, nextMonthIndex0, 1),
  };
}

/**
 * Returns the breakdown either reading the persisted host_total / fee
 * columns when they are present and consistent, or computing them
 * fresh from the vendor amount when those columns are still null
 * (legacy rows that predate this fee model). We never overwrite settled
 * rows — this is purely a read-side derivation.
 */
export function deriveVendorFeeBreakdown(row: {
  vendorAmountCents: number;
  stripeFeeCents?: number | null;
  hostTotalCents?: number | null;
}): VendorFeeBreakdown {
  const vendorAmountCents = Math.max(0, Math.floor(row.vendorAmountCents || 0));
  const persistedFee = row.stripeFeeCents;
  const persistedHostTotal = row.hostTotalCents;
  if (
    typeof persistedFee === "number" &&
    Number.isFinite(persistedFee) &&
    typeof persistedHostTotal === "number" &&
    Number.isFinite(persistedHostTotal)
  ) {
    return {
      vendorAmountCents,
      padpulseFeeCents: persistedFee,
      hostTotalCents: persistedHostTotal,
      vendorPayoutCents: Math.max(0, vendorAmountCents - persistedFee),
    };
  }
  return computeVendorFeeBreakdown(vendorAmountCents);
}

/**
 * Formats a partner's commission for display, honoring the commission model.
 *
 * Partners use one of two commission models (see `partners.commissionType` in
 * `shared/schema.ts`):
 *   - `"flat"`   → a fixed dollar amount (e.g. lenders/brokers: "$1,000")
 *   - `"percent"`→ a percentage of the partner's gross commission on the deal
 *     (e.g. realtors: "25% of gross commission")
 *
 * This is the single source of truth so no surface ever renders a percent-type
 * commission as "$25" (a 25% realtor rate) or a flat amount as "1,000%".
 *
 * `commissionType` is nullable on older rows and defaults to `"flat"`.
 */
export function formatPartnerCommission(
  commissionType: string | null | undefined,
  commissionAmount: string | null | undefined,
): string {
  const amount = parseFloat(commissionAmount ?? "0");
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  if ((commissionType ?? "flat") === "percent") {
    return `${safeAmount.toLocaleString("en-US", { maximumFractionDigits: 2 })}% of gross commission`;
  }
  return `$${safeAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Task #3736 — Effective PM fee resolver ───────────────────────────────────
// Merges per-owner billing overrides on top of the PM-level account defaults.
// Returns a ready-to-use fee config in a normalised shape (numbers not strings).

export interface EffectiveFeeConfig {
  mgmtFeeType: "flat" | "percent";
  mgmtFeeAmount: number;
  vendorUpchargePercent: number;
  categoryUpcharges: Partial<Record<string, number>>;
  source: "override" | "account_default" | "system_default";
}

export function resolveEffectiveFeeConfig(
  pmFeeSettings: { mgmtFeeType: string; mgmtFeeAmount: string | number; vendorUpchargePercent: string | number; categoryUpcharges?: Record<string, number> | null } | null | undefined,
  billingOverride?: { overrideMgmtFee: boolean; mgmtFeeType?: string | null; mgmtFeeAmount?: string | number | null; overrideVendorUpcharge: boolean; vendorUpchargePercent?: string | number | null; categoryUpcharges?: Record<string, number> | null } | null,
): EffectiveFeeConfig {
  const defaults: EffectiveFeeConfig = {
    mgmtFeeType: "percent",
    mgmtFeeAmount: 0,
    vendorUpchargePercent: 0,
    categoryUpcharges: {},
    source: "system_default",
  };

  let base: EffectiveFeeConfig = defaults;
  if (pmFeeSettings) {
    base = {
      mgmtFeeType: (pmFeeSettings.mgmtFeeType as "flat" | "percent") ?? "percent",
      mgmtFeeAmount: parseFloat(String(pmFeeSettings.mgmtFeeAmount ?? 0)) || 0,
      vendorUpchargePercent: parseFloat(String(pmFeeSettings.vendorUpchargePercent ?? 0)) || 0,
      categoryUpcharges: pmFeeSettings.categoryUpcharges ?? {},
      source: "account_default",
    };
  }

  if (!billingOverride) return base;

  const result = { ...base };
  if (billingOverride.overrideMgmtFee && billingOverride.mgmtFeeType != null) {
    result.mgmtFeeType = billingOverride.mgmtFeeType as "flat" | "percent";
    result.mgmtFeeAmount = parseFloat(String(billingOverride.mgmtFeeAmount ?? 0)) || 0;
    result.source = "override";
  }
  if (billingOverride.overrideVendorUpcharge) {
    result.vendorUpchargePercent = parseFloat(String(billingOverride.vendorUpchargePercent ?? 0)) || 0;
    if (billingOverride.categoryUpcharges) {
      result.categoryUpcharges = billingOverride.categoryUpcharges;
    }
    result.source = "override";
  }
  return result;
}

// ─── Founder-tier overage billing ─────────────────────────────────────────────
// Founders pre-pay a one-time fee for an included room allotment. Any rooms
// above that allotment ("overage rooms") are billed at the Professional rate
// minus the permanent 20% founder discount.
//   monthly: $18 × 0.80 = $14.40 / room / month
//   annual:  $15 × 0.80 = $12.00 / room / month (billed as $144/room/year)
//
// This constant mirrors FOUNDER_OVERAGE_DISCOUNT in shared/schema.ts —
// keeping it here as well so the billing-settlement engine (and any other
// server-side code that only imports from fees.ts) can access it without
// pulling in the full schema bundle.

export const FOUNDER_OVERAGE_DISCOUNT = 0.20;

/**
 * Number of rooms above a founder's free allotment that will be billed.
 * Returns 0 when the host is at or below their free tier.
 */
export function founderOverageRoomCount(
  billableRooms: number,
  founderFreeRooms: number,
): number {
  return Math.max(0, billableRooms - founderFreeRooms);
}

/**
 * Per-room monthly overage rate for a founder, honoring the billing cycle.
 *   monthly → Professional monthly rate × (1 − 20%) = $18 × 0.80 = $14.40
 *   annual  → Professional annual  rate × (1 − 20%) = $15 × 0.80 = $12.00
 *
 * The annual figure is returned as a per-month number so callers can display
 * "$/room/month" uniformly and multiply by 12 when they need the annual total.
 */
export function founderOveragePerRoomMonthlyRate(cycle: string): number {
  if (cycle === "annual") {
    return PLAN_PER_ROOM_RATES.Professional.annual * (1 - FOUNDER_OVERAGE_DISCOUNT);
  }
  return PLAN_PER_ROOM_RATES.Professional.monthly * (1 - FOUNDER_OVERAGE_DISCOUNT);
}

// ──────────────────────────────────────────────────────────────────────────────

/**
 * Pick the upcharge percent that applies to a single vendor job, honoring a
 * per-category override when the task's category has one configured. Falls
 * back to the global `vendorUpchargePercent`. Returns a number (e.g. 15 = 15%).
 *
 * `category` is the task's free-form category string; only an exact match
 * against a configured `categoryUpcharges` key takes precedence.
 */
export function resolveCategoryUpchargePercent(
  config: EffectiveFeeConfig,
  category: string | null | undefined,
): number {
  if (category) {
    const perCategory = config.categoryUpcharges?.[category];
    if (typeof perCategory === "number" && Number.isFinite(perCategory)) {
      return Math.max(0, perCategory);
    }
  }
  return Math.max(0, Number.isFinite(config.vendorUpchargePercent) ? config.vendorUpchargePercent : 0);
}

/**
 * Compute the PM's upcharge (in cents) added on top of a vendor invoice before
 * it lands on the owner's statement. `upchargePercent` is a whole-number
 * percent (15 = 15%). Negative / non-finite inputs clamp to 0; the vendor
 * amount is floored to integer cents and the result rounded to the nearest cent.
 */
export function computeOwnerUpchargeCents(vendorAmountCents: number, upchargePercent: number): number {
  const cleanAmount = Number.isFinite(vendorAmountCents) ? Math.max(0, Math.floor(vendorAmountCents)) : 0;
  const cleanPercent = Number.isFinite(upchargePercent) ? Math.max(0, upchargePercent) : 0;
  return Math.round((cleanAmount * cleanPercent) / 100);
}
